/**
 * Sanitizes HTML content using a whitelist approach to prevent XSS attacks.
 *
 * Only allows safe HTML tags and attributes. Disallowed tags are removed entirely.
 * Event handlers (onclick, onerror, etc.) and dangerous attributes are stripped.
 * Dangerous URL schemes (javascript:, data:) in href attributes are blocked.
 *
 * @param html - The HTML string to sanitize (can be undefined)
 * @returns Sanitized HTML safe for use with dangerouslySetInnerHTML
 *
 * @example
 * sanitizeHTML('<h5>Safe <u>text</u></h5>') // Returns cleaned HTML
 * sanitizeHTML('<script>alert(1)</script>') // Returns empty string
 * sanitizeHTML('<a href="javascript:alert(1)">click</a>') // href removed
 */
export const sanitizeHTML = (html: string | undefined): string => {
  if (!html) {
    return "";
  }

  const ALLOWED_TAGS = new Set([
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "br",
    "hr",
    "strong",
    "em",
    "u",
    "b",
    "i",
    "s",
    "a",
    "span",
    "div",
    "ul",
    "ol",
    "li",
    "blockquote",
    "pre",
    "code"
  ]);

  const ALLOWED_ATTR = new Set(["href", "target", "rel", "class"]);

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const sanitizeNode = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;

      if (!ALLOWED_TAGS.has(element.tagName.toLowerCase())) {
        element.remove();
        return;
      }

      Array.from(element.attributes).forEach((attr) => {
        if (!ALLOWED_ATTR.has(attr.name.toLowerCase())) {
          element.removeAttribute(attr.name);
        }
      });

      if (element.tagName.toLowerCase() === "a") {
        const href = element.getAttribute("href");
        if (
          href &&
          (href.startsWith("javascript:") || href.startsWith("data:"))
        ) {
          element.removeAttribute("href");
        }

        if (element.getAttribute("target") === "_blank") {
          element.setAttribute("rel", "noopener noreferrer");
        }
      }

      Array.from(element.childNodes).forEach(sanitizeNode);
    }
  };

  Array.from(doc.body.childNodes).forEach(sanitizeNode);

  return doc.body.innerHTML;
};
